// Skill-tree dependency wiring, shared by the AI ability builder, the ready-made
// templates, and the chat suggestion applier. Skills are authored/generated with
// dependencies expressed by NAME (or none); this assigns each skill a stable local
// id and resolves `dependsOn` to those local ids, which the apply step then maps to
// real store ids. Dependencies always point to the IMMEDIATELY PRECEDING tier — the
// store's `validateDependencies` rejects any non-adjacent prerequisite, so a cross-tier
// ref (which the AI sometimes emits) would throw at apply time and drop the rest of the
// dependency pass. Keeping deps adjacent-only here makes the graph acyclic, keeps the
// pyramid skill tree readable, and guarantees every wired dep survives validation.

export const SKILL_LEVEL_ORDER = [
  'foundation',
  'developing',
  'intermediate',
  'proficient',
  'advanced',
  'expert',
  'mastery',
] as const;

export function normalizeSkillLevelName(level: any): string {
  if (typeof level === 'string') {
    const lc = level.toLowerCase().trim();
    if ((SKILL_LEVEL_ORDER as readonly string[]).includes(lc)) return lc;
  }
  const n = Number(level);
  if (n >= 1 && n <= 7) return SKILL_LEVEL_ORDER[n - 1];
  return 'foundation';
}

/**
 * Take grouped levels — `[{ level, skills: [{ name, description, dependsOn?: string[]
 * (prerequisite skill NAMES), requirements? }] }]` — and return the same shape with
 * each skill given a stable local `id` and `dependsOn` as local ids:
 *   - explicit name references are resolved (immediately-preceding tier only; unknown/
 *     self/same-or-higher/non-adjacent refs are dropped, keeping the graph acyclic AND
 *     valid against the store's adjacent-only dependency rule), and
 *   - a skill with no usable dependencies inherits the whole previous tier (so the
 *     tree always has a logical progression — the fix for deps-less templates).
 * Levels are normalised and sorted foundation→mastery so the apply step (which maps
 * local ids → store ids in order) resolves every dependency.
 */
export function assignSkillIdsAndDeps(levels: any[]): any[] {
  const groups = (Array.isArray(levels) ? levels : [])
    .map((l) => ({ level: normalizeSkillLevelName(l?.level), skills: Array.isArray(l?.skills) ? l.skills : [] }))
    .filter((g) => g.skills.length > 0)
    .sort((a, b) => SKILL_LEVEL_ORDER.indexOf(a.level as any) - SKILL_LEVEL_ORDER.indexOf(b.level as any));

  const nameToId = new Map<string, string>();
  const idToTier = new Map<string, number>();

  const withIds = groups.map((g, ti) => ({
    level: g.level,
    skills: g.skills.map((s: any, si: number) => {
      const id = `sk_${ti}_${si}`;
      const name = String(s?.name ?? '').trim();
      if (name) nameToId.set(name.toLowerCase(), id);
      idToTier.set(id, ti);
      return { ...s, id, name };
    }),
  }));

  for (let ti = 0; ti < withIds.length; ti++) {
    const priorIds = ti > 0 ? withIds[ti - 1].skills.map((s: any) => s.id) : [];
    for (const skill of withIds[ti].skills) {
      const explicit = Array.isArray(skill.dependsOn) ? skill.dependsOn : [];
      let deps = explicit
        .map((ref: any) => (typeof ref === 'string' ? nameToId.get(ref.trim().toLowerCase()) : undefined))
        .filter((id: any): id is string => !!id && id !== skill.id && idToTier.get(id) === ti - 1);

      // No usable explicit deps → inherit the previous tier (logical progression).
      if (deps.length === 0 && priorIds.length > 0) deps = priorIds;

      skill.dependsOn = Array.from(new Set(deps));
    }
  }

  return withIds;
}
